import { api } from './api';
import type { PaginatedResponse } from '../types';

// Types
export interface DesignationMaster {
  _id: string;
  name: string;
  designationCd: string;
  appId?: string | null;
  isDelete?: boolean;
  createdBy?: string;
  createdAt?: string;
  createdIp?: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
  updatedIp?: string | null;
  deletedBy?: string | null;
  deletedAt?: string | null;
}

export interface DesignationMasterCreateRequest {
  name: string;
  designationCd: string;
  appId?: string;
}

export interface DesignationMasterUpdateRequest {
  name?: string;
  designationCd?: string;
}

export interface DesignationMasterQueryParams {
  search?: string;
  include_deleted?: boolean;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: string;
  app_id?: string;   // Filter to one accessible project (intersected server-side)
  include_global?: boolean;   // Also include core/global rows (appId=null)
}

/** Re-scope preview: which projects are aligned to a designation. */
export interface RescopePreview {
  canRescope: boolean;
  conflicts: Record<string, { personnel: number }>;
  references: Record<string, { personnel: number }>;
}

interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
  error?: {
    errorCode: string;
    details?: string;
  };
}

export const designationMasterService = {
  /**
   * Get all designation masters with optional filters
   * GET /api/v1/designations/list
   */
  getAll: async (params?: DesignationMasterQueryParams): Promise<PaginatedResponse<DesignationMaster>> => {
    const response = await api.get<PaginatedResponse<DesignationMaster>>('/api/v1/designations/list', { params });
    return response.data;
  },

  // Preview re-scoping a designation to a project (platform admin only).
  getRescopePreview: async (id: string, appId?: string | null): Promise<RescopePreview> => {
    const response = await api.get<{ data: RescopePreview }>(
      `/api/v1/designations/${id}/rescope-preview`,
      { params: { app_id: appId || undefined } }
    );
    return response.data.data;
  },

  // Change a designation's owning project (null/empty = global). Platform admin only.
  rescopeProject: async (id: string, appId: string | null): Promise<DesignationMaster> => {
    const response = await api.patch<{ data: DesignationMaster }>(
      `/api/v1/designations/${id}/project`,
      { appId: appId || null }
    );
    return response.data.data;
  },

  /**
   * Get designation master by ID
   * GET /api/v1/designations/get/{id}
   */
  getById: async (id: string): Promise<DesignationMaster> => {
    const response = await api.get<ApiResponse<DesignationMaster>>(`/api/v1/designations/get/${id}`);
    return response.data.data;
  },

  /**
   * Create new designation master
   * POST /api/v1/designations/create
   */
  create: async (data: DesignationMasterCreateRequest): Promise<DesignationMaster> => {
    const response = await api.post<ApiResponse<DesignationMaster>>('/api/v1/designations/create', data);
    return response.data.data;
  },

  /**
   * Update designation master
   * PUT /api/v1/designations/update/{id}
   */
  update: async (id: string, data: DesignationMasterUpdateRequest): Promise<DesignationMaster> => {
    const response = await api.put<ApiResponse<DesignationMaster>>(`/api/v1/designations/update/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete designation master (soft delete)
   * DELETE /api/v1/designations/delete/{id}
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/designations/delete/${id}`);
  },

  /**
   * Restore soft-deleted designation master
   * PATCH /api/v1/designations/restore/{id}
   */
  restore: async (id: string): Promise<DesignationMaster> => {
    const response = await api.patch<ApiResponse<DesignationMaster>>(`/api/v1/designations/restore/${id}`);
    return response.data.data;
  },
};
